export type DeviceCategory = 'mobile' | 'tablet' | 'desktop' | 'unknown'

export type FeedbackLocationPayload = {
  latitude: number
  longitude: number
  accuracy_m?: number
}

export type FeedbackClientContextPayload = {
  user_agent: string
  device_category: DeviceCategory
  location?: FeedbackLocationPayload
}

export function getDeviceCategory(): DeviceCategory {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  const tablet =
    /iPad|Tablet|PlayBook|Kindle|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))
  const mobile = /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  if (tablet) return 'tablet'
  if (mobile) return 'mobile'
  return 'desktop'
}

/**
 * Optional browser geolocation (user may deny or timeout). Does not throw.
 */
export function tryCaptureGeolocation(timeoutMs = 8000): Promise<FeedbackLocationPayload | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    const done = (value: FeedbackLocationPayload | null) => {
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => done(null), timeoutMs)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords
        const out: FeedbackLocationPayload = { latitude, longitude }
        if (accuracy != null && Number.isFinite(accuracy)) {
          out.accuracy_m = Math.round(accuracy * 10) / 10
        }
        done(out)
      },
      () => done(null),
      { enableHighAccuracy: false, timeout: timeoutMs - 500, maximumAge: 300_000 }
    )
  })
}

export async function buildFeedbackClientContext(): Promise<FeedbackClientContextPayload> {
  const location = await tryCaptureGeolocation()
  const base: FeedbackClientContextPayload = {
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    device_category: getDeviceCategory(),
  }
  if (location) {
    base.location = location
  }
  return base
}
